import type { TableProps } from "antd";
import { Link, useNavigate } from "react-router-dom";
import styles from "./MyTeam.module.css";
import {
  TableComponent,
  ActionsContainer,
  Modal,
} from "@react-solutions-ui/findmore-ui";
import { useTeamEmployees } from "../hooks/useTeamEmployees";
import { ITeamEmployeeTable } from "../interfaces/ITeamEmployeeInterface";
import { useEffect, useRef, useState } from "react";
import AddNotePanel from "../../../components/ui/NotePanel/AddNotePanel";
import { FileTextOutlined, FormOutlined } from "@ant-design/icons";
import { Pill } from "@react-solutions-ui/findmore-ui";
import Filters from "../components/Filters/Filters";
import MobileFilters from "../components/Filters/MobileFilters";
import Stats from "../../employees/components/Stats/Stats";
import { notification } from "antd";
import { useUser } from "../../../context/UserContext";

interface NotePanelProps {
  user: string;
  employeeId: string;
  onClose: () => void;
}

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  "data-label"?: string;
};

const withLabel = (label: string): TableCellProps => ({
  "data-label": label,
});

type NotificationType = "success" | "info" | "warning" | "error";

export default function MyTeam() {
  const [api, contextHolder] = notification.useNotification();
  const { user } = useUser();
  const { data, loading } = useTeamEmployees();
  const [showAddNotePanel, setShowAddNotePanel] = useState(false);
  const [notePanelProps, setNotePanelProps] = useState<NotePanelProps | null>(
    null
  );
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const navigate = useNavigate();

  // Filters
  const [selectedState, setSelectedState] =
    useState<string>("Todos os estados");
  const [search, setSearch] = useState<string>("");
  const [filteredData, setFilteredData] = useState<ITeamEmployeeTable[] | null>(
    null
  );
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [orderFilter, setOrderFilter] = useState<string>("Nome (A-Z)");
  const [lastAppliedFilters, setLastAppliedFilters] = useState<string[]>([
    "Todos os estados",
    "",
    "Nome (A-Z)",
  ]);
  const clearButtonClickedRef = useRef(false);
  const prevSearchRef = useRef(search);
  const suppressNextNotificationRef = useRef(false);
  const canViewInProgressReports = user?.typeLabel === "Team Leader";

  const activeFiltersCount =
    Number(selectedState !== "Todos os estados") +
    Number(search !== "") +
    Number(orderFilter !== "Nome (A-Z)");

  const hasActiveFilters = activeFiltersCount > 0;

  const sameFilters =
    lastAppliedFilters[0] === selectedState &&
    lastAppliedFilters[1] === search &&
    lastAppliedFilters[2] === orderFilter;

  const occurringEvaluations = data?.filter(
    (employee) => employee.evaluationStatus === "In progress"
  )?.length;

  const employeesToEvaluate = data?.filter(
    (employee) =>
      (employee.evaluatorStatus === "Pendente" ||
        employee.evaluatorStatus === "Em curso") &&
      employee.evaluationStatus === "In progress"
  )?.length;

  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
    description: string
  ) => {
    api[type]({
      message: message,
      description: description,
      duration: 3,
      placement: "bottomRight",
    });
  };

  const handleClearFilters = () => {
    clearButtonClickedRef.current = true;
    setSelectedState("Todos os estados");
    setSearch("");
    setOrderFilter("Nome (A-Z)");
    setFilteredData(null);
    setLastAppliedFilters(["Todos os estados", "", "Nome (A-Z)"]);
    setShowMobileFilters(false);
  };

  const columns: TableProps<ITeamEmployeeTable>["columns"] = [
    {
      title: "Nome",
      dataIndex: "employeeName",
      key: "employeeName",
      onCell: () => withLabel("Nome"),
      render: (_, record) => (
        <div className={styles.employeeNameCell}>
          <Link
            to={`/profile-team/${record.employeeId}`}
            className={`${styles.nameText} ${styles.employeeNameLink}`}
          >
            {record.employeeName}
          </Link>
          <ActionsContainer mobile={true}>
            <div className={styles.buttonsContainer}>
              <button
                className={styles.addNoteButton}
                onClick={() => {
                  setNotePanelProps({
                    user: record.employeeName,
                    employeeId: record.employeeId,
                    onClose: () => setShowAddNotePanel(false),
                  });
                  setShowAddNotePanel(true);
                }}
              >
                <FormOutlined style={{ fontSize: "20px" }} />
              </button>
              <button
                className={`${styles.evaluateButton} ${
                  (record.evaluatorStatus === "Em curso" &&
                    record.evaluationStatus === "In progress") ||
                  (canViewInProgressReports &&
                    record.evaluationStatus === "In progress") ||
                  record.evaluationStatus === "Completed"
                    ? styles.evaluateButtonInProgress
                    : styles.evaluateButtonNotStarted
                }`}
                disabled={
                  record.evaluationStatus === "Not started" ||
                  (record.evaluatorStatus === "Pendente" &&
                    record.evaluationStatus === "In progress" &&
                    !canViewInProgressReports)
                }
                onClick={() => {
                  if (
                    canViewInProgressReports &&
                    record.evaluationStatus === "In progress"
                  ) {
                    navigate(`/report/${record.evaluationId}`);
                    return;
                  }

                  if (
                    record.evaluatorStatus === "Em curso" &&
                    record.evaluationStatus === "In progress"
                  ) {
                    navigate(`/questionnaire?id=${record.evaluatorId}`);
                  } else if (record.evaluationStatus === "Completed") {
                    navigate(`/report/${record.evaluationId}`);
                  }
                }}
              >
                {record.evaluationStatus === "Completed" ? (
                  <svg
                    width="22"
                    height="26"
                    viewBox="0 0 22 26"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_3657_11418)">
                      <path
                        d="M21.6691 17.0442C19.9129 13.3447 17.2583 11.4829 13.6996 11.4829C10.139 11.4829 7.48621 13.3447 5.73002 17.046C5.65958 17.1952 5.62305 17.3581 5.62305 17.5231C5.62305 17.688 5.65958 17.8509 5.73002 18.0001C7.48621 21.6996 10.1409 23.5614 13.6996 23.5614C17.2601 23.5614 19.9129 21.6996 21.6691 17.9982C21.8118 17.6981 21.8118 17.3499 21.6691 17.0442ZM13.6996 22.2275C10.7115 22.2275 8.52363 20.7122 6.98047 17.5221C8.52363 14.3321 10.7115 12.8167 13.6996 12.8167C16.6877 12.8167 18.8755 14.3321 20.4187 17.5221C18.8774 20.7122 16.6895 22.2275 13.6996 22.2275ZM13.6255 14.2617C11.8248 14.2617 10.365 15.7215 10.365 17.5221C10.365 19.3228 11.8248 20.7826 13.6255 20.7826C15.4261 20.7826 16.8859 19.3228 16.8859 17.5221C16.8859 15.7215 15.4261 14.2617 13.6255 14.2617ZM13.6255 19.597C12.4788 19.597 11.5506 18.6688 11.5506 17.5221C11.5506 16.3754 12.4788 15.4473 13.6255 15.4473C14.7722 15.4473 15.7003 16.3754 15.7003 17.5221C15.7003 18.6688 14.7722 19.597 13.6255 19.597Z"
                        fill="#186BCE"
                      />
                    </g>
                    <path
                      d="M9.40137 0C9.57621 0 9.74273 0.0700139 9.86621 0.193359L14.2959 4.62305C14.4193 4.74644 14.4891 4.91505 14.4893 5.08984V10.1484C14.3806 10.1454 14.2712 10.1436 14.1611 10.1436C13.7676 10.1436 13.3829 10.1625 13.0078 10.2012V6.79297H8.56152C8.33226 6.79295 8.11231 6.70118 7.9502 6.53906C7.78819 6.37695 7.69727 6.15692 7.69727 5.92773V1.48242H1.48047V16.9609H4.59766C4.51957 17.1373 4.47852 17.3288 4.47852 17.5225C4.47861 17.7237 4.52212 17.9225 4.60645 18.1045C4.65981 18.219 4.71478 18.3317 4.76953 18.4434H0.657227C0.293017 18.4432 -0.000976562 18.1484 -0.000976562 17.7842V0.658203C-0.000722587 0.294178 0.293174 0.000139935 0.657227 0H9.40137ZM7.0791 11.4033C7.16967 11.4033 7.24414 11.4778 7.24414 11.5684V12.5557C7.24414 12.6462 7.16967 12.7207 7.0791 12.7207H3.29199C3.20151 12.7206 3.12793 12.6462 3.12793 12.5557V11.5684C3.12793 11.4779 3.20151 11.4034 3.29199 11.4033H7.0791ZM11.1963 8.60352C11.2868 8.60352 11.3613 8.67801 11.3613 8.76855V9.75684C11.3612 9.84731 11.2868 9.9209 11.1963 9.9209H3.29199C3.20158 9.92079 3.12805 9.84724 3.12793 9.75684V8.76855C3.12796 8.67808 3.20153 8.60362 3.29199 8.60352H11.1963ZM9.09668 5.39258H12.9707L9.09668 1.51953V5.39258Z"
                      fill="#186BCE"
                    />
                    <defs>
                      <clipPath id="clip0_3657_11418">
                        <rect
                          width="16.5986"
                          height="16.5986"
                          fill="white"
                          transform="translate(5.40039 9.22168)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                ) : (
                  <FileTextOutlined style={{ fontSize: "20px" }} />
                )}
              </button>
            </div>
          </ActionsContainer>
        </div>
      ),
      sorter: (a: ITeamEmployeeTable, b: ITeamEmployeeTable) =>
        a.employeeName.localeCompare(b.employeeName),
    },
    {
      title: "Departamento",
      dataIndex: "areaName",
      key: "areaName",
      onCell: () => withLabel("Departamento"),
      render: (text: string) => <p className={styles.text}>{text || ""}</p>,
    },
    {
      title: "Início da avaliação",
      dataIndex: "nextEvaluationDate",
      key: "nextEvaluationDate",
      onCell: () => withLabel("Início da avaliação"),
      render: (text: Date) => {
        const date = new Date(text);
        date.setDate(date.getDate() - 30);
        return (
          <p className={styles.text}>
            {!isNaN(date.getTime()) ? date.toLocaleDateString("pt-PT") : ""}
          </p>
        );
      },
      sorter: (a: ITeamEmployeeTable, b: ITeamEmployeeTable) =>
        new Date(a.nextEvaluationDate).getTime() -
        new Date(b.nextEvaluationDate).getTime(),
    },
    {
      title: "Estado",
      dataIndex: "evaluationStatus",
      key: "evaluationStatus",
      onCell: () => withLabel("Estado"),
      render: (_, record) => (
        <>
          {record.evaluationStatus === "In progress" &&
          (record.evaluatorStatus === "Em curso" ||
            record.evaluatorStatus === "Pendente") ? (
            <Pill
              text="Por avaliar"
              color="rgba(24, 107, 206, 1)"
              backgroundColor="rgba(24, 107, 206, 0.1)"
            />
          ) : record.evaluatorStatus === "Concluído" &&
            record.evaluationStatus === "In progress" ? (
            <Pill
              text="Avaliado"
              color="rgba(114, 46, 209, 1)"
              backgroundColor="rgba(249, 240, 255, 1)"
            />
          ) : record.evaluationStatus === "Completed" ? (
            <Pill
              text="Concluído"
              color="rgba(35, 120, 4, 1)"
              backgroundColor="rgba(246, 255, 237, 1)"
            />
          ) : (
            <Pill
              text="Por iniciar"
              color="rgba(111, 111, 118, 1)"
              backgroundColor="rgba(111, 111, 118, 0.1)"
            />
          )}
        </>
      ),
    },
    {
      title: "Ações",
      dataIndex: "actions",
      key: "actions",
      onCell: () => ({ className: "hide-on-mobile" }),
      render: (_, record) => (
        <ActionsContainer mobile={false}>
          <div className={styles.buttonsContainer}>
            <button
              className={styles.addNoteButton}
              onClick={() => {
                setNotePanelProps({
                  user: record.employeeName,
                  employeeId: record.employeeId,
                  onClose: () => setShowAddNotePanel(false),
                });
                setShowAddNotePanel(true);
              }}
            >
              Adicionar nota
            </button>
            <button
              className={`${styles.evaluateButton} ${
                (record.evaluatorStatus === "Em curso" &&
                  record.evaluationStatus === "In progress") ||
                (canViewInProgressReports &&
                  record.evaluationStatus === "In progress") ||
                record.evaluationStatus === "Completed"
                  ? styles.evaluateButtonInProgress
                  : styles.evaluateButtonNotStarted
              }`}
              disabled={
                record.evaluationStatus === "Not started" ||
                (record.evaluatorStatus === "Pendente" &&
                  record.evaluationStatus === "In progress" &&
                  !canViewInProgressReports)
              }
              onClick={() => {
                if (
                  canViewInProgressReports &&
                  record.evaluationStatus === "In progress"
                ) {
                  navigate(`/report/${record.evaluationId}`);
                  return;
                }

                if (
                  record.evaluatorStatus === "Em curso" &&
                  record.evaluationStatus === "In progress"
                ) {
                  navigate(`/questionnaire?id=${record.evaluatorId}`);
                } else if (record.evaluationStatus === "Completed") {
                  navigate(`/report/${record.evaluationId}`);
                }
              }}
            >
              {canViewInProgressReports &&
              record.evaluationStatus === "In progress"
                ? "Ver relatório"
                : ((record.evaluatorStatus === "Em curso" ||
                record.evaluatorStatus === "Pendente") &&
                record.evaluationStatus === "In progress") ||
              record.evaluationStatus === "Not started"
                ? "Avaliar"
                : "Ver relatório"}
            </button>
          </div>
        </ActionsContainer>
      ),
    },
  ];

  const getAllStates = () => {
    if (data) {
      const states = new Set<string>();
      states.add("Todos os estados");
      data.forEach((employee) => {
        if (employee.evaluationStatus === "Not started") {
          states.add("Por iniciar");
        } else if (
          employee.evaluationStatus === "In progress" &&
          (employee.evaluatorStatus === "Em curso" ||
            employee.evaluatorStatus === "Pendente")
        ) {
          states.add("Por avaliar");
        } else if (
          employee.evaluationStatus === "In progress" &&
          employee.evaluatorStatus === "Concluído"
        ) {
          states.add("Avaliado");
        } else if (employee.evaluationStatus === "Completed") {
          states.add("Concluído");
        }
      });
      return Array.from(states);
    }
    return ["Todos os estados"];
  };

  const filterByState = (data: ITeamEmployeeTable[], state: string) => {
    if (!state || state === "Todos os estados") {
      return data;
    } else if (state === "Por iniciar") {
      return data?.filter(
        (employee) =>
          employee.evaluationStatus === "Not started" ||
          (employee.evaluatorStatus === "Pendente" &&
            employee.evaluationStatus !== "Completed")
      );
    } else if (state === "Por avaliar") {
      return data?.filter(
        (employee) =>
          employee.evaluationStatus === "In progress" &&
          (employee.evaluatorStatus === "Em curso" ||
            employee.evaluatorStatus === "Pendente")
      );
    } else if (state === "Avaliado") {
      return data?.filter(
        (employee) =>
          employee.evaluationStatus === "In progress" &&
          employee.evaluatorStatus === "Concluído"
      );
    } else if (state === "Concluído") {
      return data?.filter(
        (employee) => employee.evaluationStatus === "Completed"
      );
    }
    return data;
  };

  const filterBySearch = (data: ITeamEmployeeTable[], search: string) => {
    return data?.filter(
      (employee) =>
        employee.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
        employee.areaName?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const filterByName = (data: ITeamEmployeeTable[], name: string) => {
    if (!data || data.length === 0) {
      return [];
    }
    if (name === "Nome (A-Z)") {
      return [...data].sort((a, b) =>
        (a.employeeName || "").localeCompare(b.employeeName || "")
      );
    } else if (name === "Nome (Z-A)") {
      return [...data].sort((a, b) =>
        (b.employeeName || "").localeCompare(a.employeeName || "")
      );
    }
    return [...data];
  };

  const filterByDate = (data: ITeamEmployeeTable[], date: string) => {
    if (date === "Data mais próxima") {
      return [...data].sort(
        (a, b) =>
          new Date(a.nextEvaluationDate).getTime() -
          new Date(b.nextEvaluationDate).getTime()
      );
    } else if (date === "Data mais distante") {
      return [...data].sort(
        (a, b) =>
          new Date(b.nextEvaluationDate).getTime() -
          new Date(a.nextEvaluationDate).getTime()
      );
    }
    return data || [];
  };

  const filter = () => {
    if (!data) {
      setFilteredData(null);
      return;
    }

    let filteredDataSet: ITeamEmployeeTable[] = [...data];

    if (search !== "") {
      filteredDataSet = filterBySearch(filteredDataSet, search);
    }
    if (selectedState !== "") {
      filteredDataSet = filterByState(filteredDataSet, selectedState);
    }
    if (orderFilter === "Nome (A-Z)" || orderFilter === "Nome (Z-A)") {
      filteredDataSet = filterByName(filteredDataSet, orderFilter);
    }
    if (
      orderFilter === "Data mais próxima" ||
      orderFilter === "Data mais distante"
    ) {
      filteredDataSet = filterByDate(filteredDataSet, orderFilter);
    }
    setFilteredData(filteredDataSet);

    const searchChanged = prevSearchRef.current !== search;
    prevSearchRef.current = search;

    const suppressNotifications =
      showMobileFilters || suppressNextNotificationRef.current;

    if (clearButtonClickedRef.current) {
      openNotificationWithIcon(
        "success",
        "Filtros limpos",
        "Filtros limpos com sucesso"
      );
      clearButtonClickedRef.current = false;
    } else if (suppressNotifications) {
      suppressNextNotificationRef.current = false;
      return;
    } else if (!searchChanged) {
      openNotificationWithIcon(
        "success",
        "Filtros aplicados",
        "Filtros aplicados com sucesso"
      );
    }
  };

  useEffect(() => {
    filter();
  }, [search, selectedState, orderFilter]);

  const handleDismissMobileFilters = () => {
    suppressNextNotificationRef.current = true;
    setShowMobileFilters(false);
  };

  const handleApplyMobileFilters = () => {
    suppressNextNotificationRef.current = true;
    setShowMobileFilters(false);
    setLastAppliedFilters([selectedState, search, orderFilter]);
    openNotificationWithIcon(
      "success",
      "Filtros aplicados",
      "Filtros aplicados com sucesso"
    );
  };

  return (
    <>
      {contextHolder}
      <div className={`page-container ${styles.container}`}>
        <h1 className={styles.title}>A minha equipa</h1>
        <div className={styles.infoContainer}>
          <Stats
            ongoingEvaluations={occurringEvaluations!}
            employeesToEvaluate={employeesToEvaluate!}
          />
          <div className={styles.tableContainer}>
            <Filters
              states={getAllStates()}
              setSelectedState={setSelectedState}
              selectedState={selectedState}
              setSearch={setSearch}
              search={search}
              handleClearFilters={handleClearFilters}
              setShowMobileFilters={setShowMobileFilters}
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
            />
            <TableComponent
              columns={columns}
              data={filteredData || data || []}
              loading={loading}
            />
          </div>
        </div>
      </div>
      {successModal.open && (
        <Modal
          title="SUCESSO"
          type="info"
          onCloseModal={() => setSuccessModal({ ...successModal, open: false })}
          closeModalLabel="FECHAR"
        >
          {successModal.message}
        </Modal>
      )}
      {showAddNotePanel && (
        <AddNotePanel
          user={notePanelProps?.user!}
          employeeId={notePanelProps?.employeeId!}
          onClose={() => setShowAddNotePanel(false)}
          onSuccess={(msg) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
          }}
        />
      )}
      {showMobileFilters && (
        <MobileFilters
          onDismiss={handleDismissMobileFilters}
          onApply={handleApplyMobileFilters}
          states={getAllStates()}
          setSelectedState={setSelectedState}
          selectedState={selectedState}
          orderFilter={orderFilter}
          setOrderFilter={setOrderFilter}
          hasActiveFilters={hasActiveFilters}
          sameFilters={sameFilters}
          handleClearFilters={handleClearFilters}
          lastAppliedFilters={lastAppliedFilters}
        />
      )}
    </>
  );
}
